<script setup lang="ts">
import { assign, omit } from 'radash'

import type { ModalDefaultProperties } from '~/composables/modals'

import { MODAL_COMPONENTS } from '~/components/Modal/components'
const { subscribe } = useModals()
const modalComponent = ref()
const wrapperBindings = ref<ModalDefaultProperties['wrapper'] | null>()
const modalBindings = ref()
const isOpen = ref(false)

const modal = ref()
const onClose = () => {
  isOpen.value = false
}

// Reset the modal component and bindings when the dialog is closed
// Using an emitted event works a lot better than using the previous timeout strategy
const onDialogClosed = () => {
  modalComponent.value = undefined
  modalBindings.value = undefined
  wrapperBindings.value = undefined
  closeEvent.value = undefined
}

const unsubscribe = subscribe(({ context, kind }) => {
  modalComponent.value = markRaw(MODAL_COMPONENTS[kind])

  // Apply wrapper bindings to the wrapper
  const defaultWrapperProperties: ModalDefaultProperties['wrapper'] = {
    footerAlign: 'center',
    size: 'sm',
  }
  wrapperBindings.value = assign(
    defaultWrapperProperties,
    context.wrapper || {},
  )

  // Send the rest of the bindings to the modal component
  modalBindings.value = {
    ...omit(context, ['wrapper']),
    onClose: () => {
      context.onClose?.()
      onClose()
    },
    onDialogClose: onClose,
  }

  isOpen.value = true
})

onBeforeUnmount(unsubscribe)
</script>

<template>
  <TairoModal :open="isOpen" @dialog-closed="onDialogClosed" v-bind="wrapperBindings" @close="modalBindings?.onClose">
    <template #header>
      <ModalHeader v-bind="modalBindings" />
    </template>
    <component :is="modalComponent" v-if="modalComponent" v-bind="modalBindings" />
    <template #footer v-if="modalBindings?.actions?.length">
      <ModalFooter v-bind="modalBindings" />
    </template>
  </TairoModal>
</template>
